import { palette, Popover } from "lib/miolo";
import BarChart from "components/company/sections/summary/adornments/BarChart";
import numeral from "numeral";
import type { ReactNode } from "react";
import { useState } from "react";
import type { DividendsPaidByYearDetails, DividendYieldDetails } from "types/typedDocumentNodes";

function formatDividendYield(dividendYield: number) {
  return numeral(dividendYield * 100)
    .format("0.0")
    .replace(".0", "")
    .replace(".", ",");
}

interface IDividendYieldSnapshotPopoverProps {
  tickerSymbol: string;
  dividendYield: number;
  amountPaid: number;
  children: ReactNode;
}

function DividendYieldSnapshotPopover({
  tickerSymbol,
  dividendYield,
  amountPaid,
  children,
}: IDividendYieldSnapshotPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const amountPaidFormated = numeral(amountPaid).format("0.00").replace(".", ",");

  return (
    <Popover
      id="DividendYieldSnapshotPopover"
      isOpen={isOpen}
      arrowColor={palette.white}
      backgroundColor={palette.white}
      onClose={() => setIsOpen(false)}
      render={() => {
        return (
          <div className="grid max-w-xs grid-cols-1 gap-x-3 gap-y-2 p-4 font-aeonik text-slate-500">
            <div className="p-2">
              <strong className="text-xl font-bold tracking-wider">{formatDividendYield(dividendYield)}%</strong>

              <div className="text-sm font-medium tracking-wide text-gray-500">
                {tickerSymbol} • ${amountPaidFormated} Amount Paid
              </div>
            </div>
          </div>
        );
      }}
    >
      {({ accessibility, ref }) => (
        <button
          {...accessibility}
          ref={ref}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="m-0 inline-flex cursor-pointer border-b border-gray-900 bg-transparent p-0 font-normal text-inherit hover:border-blue-600 hover:text-blue-600"
        >
          {children}
        </button>
      )}
    </Popover>
  );
}

interface IAmericanDividendsPaidSummaryProps {
  tickerSymbol: string;
  dividendsPaidByYear: DividendsPaidByYearDetails[];
  dividendYield: DividendYieldDetails[];
}
export default function AmericanDividendsPaidSummary({
  tickerSymbol,
  dividendsPaidByYear,
  dividendYield,
}: IAmericanDividendsPaidSummaryProps) {
  const [lastDividendYield] = dividendYield;

  if (!lastDividendYield) {
    return null;
  }

  return (
    <div className="flex items-center">
      <div className="font-aeonik">
        <div className="rounded-xl bg-slate-100 p-3">
          <div className="grid grid-cols-[repeat(3,max-content)] gap-x-1 text-xl text-slate-800">
            <strong className="font-bold">{formatDividendYield(lastDividendYield.dividendYield)}%</strong>
            {" • "}
            <div>
              <DividendYieldSnapshotPopover
                tickerSymbol={tickerSymbol}
                amountPaid={lastDividendYield.amountPaid}
                dividendYield={lastDividendYield.dividendYield}
              >
                {tickerSymbol}
              </DividendYieldSnapshotPopover>
            </div>
          </div>

          <span className="text-sm font-medium text-slate-500">DY of the last of 12 months</span>
        </div>
      </div>

      {lastDividendYield.dividendYield > 0 && dividendsPaidByYear.length > 1 && (
        <div className="ml-6">
          <BarChart
            ariaLabel="Gráfico demonstrando a distribuição dos Dividendos ao longo dos últimos 20 anos"
            chart={{
              margin: { top: 0, left: 0, bottom: 0, right: 0 },
              padding: { top: 4, left: 4, bottom: 4, right: 4 },
              primaryColor: palette.copper,
              width: 280,
              height: 40,
            }}
            nodes={dividendsPaidByYear
              .slice(0, 10)
              .sort((a, b) => {
                return a.fiscalYear - b.fiscalYear;
              })
              .map((node) => {
                return {
                  date: new Date(node.fiscalYear, 1, 1),
                  value: node.amountPaid ?? 0,
                };
              })}
          />
        </div>
      )}
    </div>
  );
}
